import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ScanLine } from "lucide-react";
import { http } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SectionTitle, SeverityChip } from "@/components/Kit";

export default function AppDetail() {
  const { id } = useParams();
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);

  useEffect(() => {
    if (orgId) http.get(`/applications/${id}`, { params: { org_id: orgId } }).then((r) => setApp(r.data));
  }, [id, orgId]);

  if (!app) return <div className="p-8 text-[#8A8175]">Loading…</div>;
  const of = app.open_findings;

  return (
    <div className="p-6 max-w-[1400px] mx-auto fade-up">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-[#8A8175] hover:text-[#A5300F] mb-3">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#A5300F]">{app.name}</h1>
          <p className="text-sm text-[#8A8175] mt-1">
            {app.criticality} · {app.internet_facing ? "Internet-facing" : "Internal"} · {app.data_classification} · Owner {app.business_owner || "—"}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate(`/findings?app_id=${app.id}`)} data-testid="view-findings-btn"
            className="text-sm border border-[#E7D9C6] px-3 py-2 rounded-md hover:bg-[#FBF7F1]">View Findings</button>
          <button onClick={() => navigate(`/scan?app_id=${app.id}`)}
            className="flex items-center gap-2 text-sm bg-[#A5300F] text-white font-semibold px-3 py-2 rounded-md hover:bg-[#8F2A0D]">
            <ScanLine className="w-4 h-4" /> New Scan
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        {["Critical", "High", "Medium", "Low"].map((s) => (
          <Card key={s} className="p-4">
            <SeverityChip severity={s} />
            <div className="stat-numeral text-3xl mt-2">{of[s]}</div>
          </Card>
        ))}
      </div>

      <Card className="p-5">
        <SectionTitle>Scan History</SectionTitle>
        <table className="w-full text-sm mt-3">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left border-b border-[#ECE6DD]">
            <th className="py-2">Type</th><th>File</th><th>Engine</th><th>Status</th><th>Finished</th><th className="text-right">Total</th>
          </tr></thead>
          <tbody>
            {(app.scans || []).map((s) => (
              <tr key={s.id} className="border-b border-[#F4EFE8]">
                <td className="py-2 uppercase text-xs font-semibold">{s.scan_type}</td>
                <td className="text-[#5C554C]">{s.source_filename}</td>
                <td className="text-[#8A8175] text-xs">{s.engine} {s.engine_version}</td>
                <td><span className={s.status === "complete" ? "text-[#2E7D32]" : s.status === "failed" ? "text-[#A5300F]" : "text-[#E39629]"}>{s.status}</span></td>
                <td className="text-[#8A8175] text-xs">{s.finished_at ? new Date(s.finished_at).toLocaleString("en-GB") : "—"}</td>
                <td className="text-right stat-numeral">{s.summary?.total ?? 0}</td>
              </tr>
            ))}
            {(app.scans || []).length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[#B5AC9E]">No scans yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
